import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  getBatchCalendarSettings,
  saveBatchCalendarSettings,
  resetBatchCalendarSettings,
  getBatchCalendar,
  createBatchOverride,
  deleteBatchOverride,
  getBatchUnavailability,
  reviewUnavailability,
} from "./adminCalendarService";
import "./calendar.css";
import "../courses/adminCourses.css";

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const fmtMonth = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const buildGrid = (year, month) => {
  const first = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = Array(first).fill(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

/* ── Settings Panel ── */
const SettingsPanel = ({ batchId, onUpdated }) => {
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [studyDays, setStudyDays] = useState([]);
  const [cutoff, setCutoff] = useState(24);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await getBatchCalendarSettings(batchId);
    setSettings(res.data);
    setStudyDays(res.data.studyDays ? res.data.studyDays.split(",") : []);
    setCutoff(res.data.unavailabilityCutoffHours ?? 24);
  }, [batchId]);

  useEffect(() => { load(); }, [load]);

  const toggleDay = (d) =>
    setStudyDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveBatchCalendarSettings(batchId, {
        studyDays: DAYS.filter((d) => studyDays.includes(d)).join(","),
        unavailabilityCutoffHours: Number(cutoff),
      });
      await load();
      setEditing(false);
      onUpdated?.();
    } catch (err) {
      alert(err?.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset batch calendar settings to global?")) return;
    try {
      await resetBatchCalendarSettings(batchId);
      await load();
      onUpdated?.();
    } catch (err) {
      alert(err?.response?.data?.message || "Reset failed.");
    }
  };

  if (!settings) return null;

  return (
    <div className="cal-settings-panel">
      <div className="cal-settings-row" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <span className="cal-settings-label">Study Days:</span>
          <span style={{ fontSize: "0.85rem", color: "#374151" }}>{settings.studyDays || "—"}</span>
          <span className="cal-settings-label" style={{ marginLeft: "0.5rem" }}>Cutoff:</span>
          <span style={{ fontSize: "0.85rem", color: "#374151" }}>{settings.unavailabilityCutoffHours}h</span>
          {!settings.batchOverridden && (
            <span className="ac-badge ac-badge-blue" style={{ fontSize: "0.7rem" }}>Inheriting from Global</span>
          )}
        </div>
        <div className="ac-actions">
          <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => setEditing((v) => !v)}>
            {editing ? "Cancel" : "Customize"}
          </button>
          {settings.batchOverridden && (
            <button className="ac-btn ac-btn-danger ac-btn-sm" onClick={handleReset}>
              Reset to Global
            </button>
          )}
        </div>
      </div>

      {editing && (
        <div style={{ marginTop: "0.75rem", paddingTop: "0.75rem", borderTop: "1px solid #e2e8f0" }}>
          <div className="ac-form-group">
            <label>Study Days</label>
            <div className="cal-days-group">
              {DAYS.map((d) => (
                <label key={d} className={`cal-day-check ${studyDays.includes(d) ? "checked" : ""}`}>
                  <input type="checkbox" checked={studyDays.includes(d)} onChange={() => toggleDay(d)} />
                  {d}
                </label>
              ))}
            </div>
          </div>
          <div className="ac-form-group" style={{ maxWidth: 200 }}>
            <label>Cutoff Hours</label>
            <input type="number" min={0} value={cutoff} onChange={(e) => setCutoff(e.target.value)} />
          </div>
          <button className="ac-btn ac-btn-primary ac-btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
};

/* ── Unavailability Panel ── */
const UnavailabilityPanel = ({ batchId }) => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBatchUnavailability(batchId);
      setRequests(Array.isArray(res.data) ? res.data : []);
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => { load(); }, [load]);

  const handleReview = async (id, status) => {
    try {
      await reviewUnavailability(id, { status, adminNotes: notes[id] || "" });
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || "Action failed.");
    }
  };

  const statuses = ["ALL", "PENDING", "APPROVED", "REJECTED", "EXPIRED"];
  const filtered = filter === "ALL" ? requests : requests.filter((r) => r.status === filter);

  if (loading) return <div className="ac-loading">Loading requests…</div>;

  return (
    <div>
      <div className="cal-unavail-filters">
        {statuses.map((s) => (
          <button
            key={s}
            className={`cal-filter-btn ${filter === s ? "active" : ""}`}
            onClick={() => setFilter(s)}
          >
            {s} {s !== "ALL" && `(${requests.filter((r) => r.status === s).length})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="ac-empty">No requests found.</div>
      ) : (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Date</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: "0.88rem" }}>{r.userName}</div>
                    <div style={{ fontSize: "0.78rem", color: "#9ca3af" }}>{r.userEmail}</div>
                  </td>
                  <td style={{ fontSize: "0.85rem" }}>{r.date}</td>
                  <td style={{ fontSize: "0.82rem", color: "#6b7280", maxWidth: 200 }}>{r.reason || "—"}</td>
                  <td>
                    <span className={`cal-status cal-status-${r.status}`}>{r.status}</span>
                  </td>
                  <td>
                    {r.status === "PENDING" ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                        <input
                          type="text"
                          placeholder="Admin notes (optional)"
                          value={notes[r.id] || ""}
                          onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                          style={{ padding: "0.3rem 0.5rem", border: "1px solid #d1d5db", borderRadius: 5, fontSize: "0.78rem", width: 180 }}
                        />
                        <div className="ac-actions">
                          <button className="ac-btn ac-btn-success ac-btn-sm" onClick={() => handleReview(r.id, "APPROVED")}>Approve</button>
                          <button className="ac-btn ac-btn-danger ac-btn-sm" onClick={() => handleReview(r.id, "REJECTED")}>Reject</button>
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontSize: "0.78rem", color: "#9ca3af" }}>{r.adminNotes || "—"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ── Main Page ── */
const AdminBatchCalendarPage = () => {
  const { batchId } = useParams();
  const today = new Date();
  const [cur, setCur] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [calDays, setCalDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("calendar");
  const [calKey, setCalKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBatchCalendar(batchId, fmtMonth(cur));
      setCalDays(Array.isArray(res.data) ? res.data : []);
    } finally {
      setLoading(false);
    }
  }, [batchId, cur]);

  useEffect(() => { load(); }, [load]);

  const dayMap = Object.fromEntries(calDays.map((d) => [d.date, d]));

  const handleClick = async (day) => {
    if (!day) return;
    const dateStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const existing = dayMap[dateStr];
    const newType = existing?.dayType === "STUDY_DAY" ? "HOLIDAY" : "STUDY_DAY";
    const reason = window.prompt(`Set ${dateStr} as ${newType} — reason (optional):`, "") ?? "";
    if (reason === null) return;
    try {
      await createBatchOverride(batchId, { date: dateStr, dayType: newType, reason });
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || "Action failed.");
    }
  };

  const handleRightClick = async (e, day) => {
    e.preventDefault();
    if (!day) return;
    const dateStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const existing = dayMap[dateStr];
    if (!existing || existing.source === "DEFAULT") return;
    if (!window.confirm(`Remove batch override for ${dateStr}?`)) return;
    try {
      await deleteBatchOverride(batchId, existing.id || existing.overrideId);
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || "Action failed.");
    }
  };

  const cells = buildGrid(cur.getFullYear(), cur.getMonth());
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const srcClass = (src) =>
    src === "BATCH_OVERRIDE" ? "cal-src-batch" : src === "GLOBAL_OVERRIDE" ? "cal-src-global" : "cal-src-default";
  const srcLabel = (src) =>
    src === "BATCH_OVERRIDE" ? "Batch" : src === "GLOBAL_OVERRIDE" ? "Global" : "Default";

  return (
    <div>
      <div className="ac-header">
        <h2>Batch Calendar</h2>
      </div>

      <SettingsPanel key={calKey} batchId={batchId} onUpdated={() => { setCalKey((k) => k + 1); load(); }} />

      {/* Tabs */}
      <div style={{ display: "flex", gap: "0.25rem", marginBottom: "1rem", borderBottom: "2px solid #e5e7eb" }}>
        {["calendar", "unavailability"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: "0.5rem 1.1rem", border: "none", background: "none", cursor: "pointer",
              fontWeight: activeTab === tab ? 700 : 400,
              color: activeTab === tab ? "#2563eb" : "#6b7280",
              borderBottom: activeTab === tab ? "2px solid #2563eb" : "2px solid transparent",
              marginBottom: "-2px", fontSize: "0.9rem", textTransform: "capitalize",
            }}
          >
            {tab === "calendar" ? "Calendar" : "Unavailability Requests"}
          </button>
        ))}
      </div>

      {activeTab === "calendar" && (
        <>
          <div className="cal-wrap">
            <div className="cal-nav">
              <button className="cal-nav-btn" onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() - 1, 1))}>‹</button>
              <h3>{cur.toLocaleString("default", { month: "long", year: "numeric" })}</h3>
              <button className="cal-nav-btn" onClick={() => setCur(new Date(cur.getFullYear(), cur.getMonth() + 1, 1))}>›</button>
            </div>
            <div className="cal-dow-row">
              {DOW.map((d) => <div key={d} className="cal-dow-cell">{d}</div>)}
            </div>
            {loading ? (
              <div className="ac-loading">Loading…</div>
            ) : (
              <div className="cal-grid">
                {cells.map((day, i) => {
                  if (!day) return <div key={i} className="cal-cell cal-empty" />;
                  const dateStr = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const info = dayMap[dateStr];
                  const cls = info?.dayType === "STUDY_DAY" ? "cal-study" : info?.dayType === "HOLIDAY" ? "cal-holiday" : "";
                  const isToday = dateStr === todayStr;
                  return (
                    <div
                      key={i}
                      className={`cal-cell ${cls} ${isToday ? "cal-today" : ""}`}
                      onClick={() => handleClick(day)}
                      onContextMenu={(e) => handleRightClick(e, day)}
                      title={`${dateStr}${info ? ` — ${info.dayType} (${info.source})` : ""}`}
                    >
                      <div className="cal-day-num">{day}</div>
                      {info && (
                        <>
                          <span className={`cal-dot cal-dot-${info.dayType === "STUDY_DAY" ? "study" : "holiday"}`} />
                          <span style={{ fontSize: "0.65rem", color: info.dayType === "STUDY_DAY" ? "#15803d" : "#b91c1c" }}>
                            {info.dayType === "STUDY_DAY" ? "Study" : "Holiday"}
                          </span>
                          <div>
                            <span className={`cal-src ${srcClass(info.source)}`}>{srcLabel(info.source)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ marginTop: "0.75rem", display: "flex", gap: "1rem", fontSize: "0.8rem", color: "#6b7280", flexWrap: "wrap" }}>
            <span><span className="cal-dot cal-dot-study" /> Study Day</span>
            <span><span className="cal-dot cal-dot-holiday" /> Holiday</span>
            <span><span className="cal-src cal-src-batch">Batch</span> Batch override</span>
            <span><span className="cal-src cal-src-global">Global</span> Global override</span>
            <span><span className="cal-src cal-src-default">Default</span> From settings</span>
            <span style={{ color: "#9ca3af" }}>Left-click to toggle · Right-click to remove batch override</span>
          </div>
        </>
      )}

      {activeTab === "unavailability" && <UnavailabilityPanel batchId={batchId} />}
    </div>
  );
};

export default AdminBatchCalendarPage;
